// Leaderboard.js
// Mirrors LeaderboardView.swift + LeaderboardView_DraftStreak.swift.
//
// Data: live Firestore — Drafts, Streaks, and Knockout boards read real
// leaderboard_snapshots / knockout_leaderboard documents. Other modes show a
// "coming soon" path until those screens ship on web.
//
// Layout (top → bottom):
//   Trophy button (top-right) → daily/monthly champions modal
//   Sport filter row (5 sport icon pills)
//   Filters row: game mode dropdown (left) + timeframe dropdown (right)
//   User stat card (rank + score) — shown when user has a score
//   Leaderboard panel: column header + scrollable entry rows

import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../managers/AuthContext";
import { loadLeaderboardSnapshot, loadKnockoutLeaderboard, loadPickUpStandings } from "../managers/leaderboardService";
import DailyChampionsModal from "../components/DailyChampionsModal";
import trophyFill from "../assets/trophy-fill.svg";
import footballIcon from "../assets/football.svg";
import baseballIcon from "../assets/baseball-fill.svg";
import basketballIcon from "../assets/dribbble-logo-fill.svg";
import hockeyIcon from "../assets/hockey-fill.svg";
import soccerIcon from "../assets/soccer-ball-fill.svg";

// ─── Constants ────────────────────────────────────────────────────────────────

const SPORTS = [
  { id: "football",   name: "Football",   hex: "#00b04f" },
  { id: "baseball",   name: "Baseball",   hex: "#db2626" },
  { id: "basketball", name: "Basketball", hex: "#ff8c00" },
  { id: "hockey",     name: "Hockey",     hex: "#0da6e8" },
  { id: "soccer",     name: "Soccer",     hex: "#1a66e6" },
];

// Sport icons — same SVG set used on Home and Draft.
const SPORT_ICONS = {
  football:   footballIcon,
  baseball:   baseballIcon,
  basketball: basketballIcon,
  hockey:     hockeyIcon,
  soccer:     soccerIcon,
};

const GAME_MODES = ["DRAFTS", "STREAKS", "STAT ATTACK", "RECON", "GAUNTLET", "DYNASTY", "PICK-UP", "KNOCKOUT"];
const TIMEFRAMES = ["DAILY", "MONTHLY", "ALL-TIME"];

const APP_THEME = "#d4f53c";

// ─── Main Leaderboard Screen ──────────────────────────────────────────────────

export default function Leaderboard() {
  const { user } = useAuth();
  const location = useLocation();
  // Default to the sport passed in (e.g. from a completed draft), else Football.
  const initialSport = SPORTS.find(s => s.id === location.state?.sport?.id) || SPORTS[0];
  const [selectedSport, setSelectedSport] = useState(initialSport);
  const [selectedGameMode, setSelectedGameMode] = useState("DRAFTS");
  const [selectedTimeframe, setSelectedTimeframe] = useState("DAILY");
  const [showGameModeDropdown, setShowGameModeDropdown] = useState(false);
  const [showTimeframeDropdown, setShowTimeframeDropdown] = useState(false);
  const [hasAppeared, setHasAppeared] = useState(false);
  const [entries, setEntries] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [showChampions, setShowChampions] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHasAppeared(true), 100);
    return () => clearTimeout(t);
  }, []);

  // ─── Load real leaderboard data ───────────────────────────────────────────
  const loadData = useCallback(async () => {
    // Knockout: all-time pool wins (no timeframe). Reads knockout_leaderboard.
    if (selectedGameMode === "KNOCKOUT") {
      setIsLoadingData(true);
      setLoadError(null);
      try {
        const result = await loadKnockoutLeaderboard(selectedSport.id);
        const enriched = result.entries.map(e => ({
          ...e,
          teamRating: e.totalWins, // panel + stat card read `teamRating`
          isCurrentUser: user ? e.userID === user.uid : false,
        }));
        setEntries(enriched);
        setLastUpdated(null);
      } catch (err) {
        console.error("Knockout leaderboard load failed:", err);
        setLoadError("Couldn't load leaderboard. Check your connection.");
      } finally {
        setIsLoadingData(false);
      }
      return;
    }

    // Pick Up: all-time W-L standings (no timeframe). Reads pickup_standings.
    if (selectedGameMode === "PICK-UP") {
      setIsLoadingData(true);
      setLoadError(null);
      try {
        const standings = await loadPickUpStandings(selectedSport.id);
        // wins desc → win% desc → fewest losses → alphabetical (matches LeaderboardView_PickUp)
        standings.sort((a, b) => {
          if (a.wins !== b.wins) return b.wins - a.wins;
          if (a.winPct !== b.winPct) return b.winPct - a.winPct;
          if (a.losses !== b.losses) return a.losses - b.losses;
          return a.username.localeCompare(b.username);
        });
        const enriched = standings.map((e) => ({
          ...e,
          isCurrentUser: user ? e.userID === user.uid : false,
        }));
        setEntries(enriched);
        setLastUpdated(null);
      } catch (err) {
        console.error("Pick Up leaderboard load failed:", err);
        setLoadError("Couldn't load leaderboard. Check your connection.");
      } finally {
        setIsLoadingData(false);
      }
      return;
    }

    const live = selectedGameMode === "DRAFTS" || selectedGameMode === "STREAKS";
    if (!live) { setEntries([]); return; }
    setIsLoadingData(true);
    setLoadError(null);
    try {
      const gameMode = selectedGameMode === "STREAKS" ? "streak" : "draft";
      const result = await loadLeaderboardSnapshot({
        sport: selectedSport,
        timeframe: selectedTimeframe,
        gameMode,
      });
      // Mark the current user's entry
      const enriched = result.entries.map(e => ({
        ...e,
        isCurrentUser: user ? e.userID === user.uid : false,
      }));
      setEntries(enriched);
      setLastUpdated(result.lastUpdated);
    } catch (err) {
      console.error("Leaderboard load failed:", err);
      setLoadError("Couldn't load leaderboard. Check your connection.");
    } finally {
      setIsLoadingData(false);
    }
  }, [selectedSport, selectedGameMode, selectedTimeframe, user]);

  useEffect(() => { loadData(); }, [loadData]);

  const isDraftsMode = selectedGameMode === "DRAFTS";
  const isStreaksMode = selectedGameMode === "STREAKS";
  const isKnockoutMode = selectedGameMode === "KNOCKOUT";
  const isPickUpMode = selectedGameMode === "PICK-UP";
  const isLiveMode = isDraftsMode || isStreaksMode || isKnockoutMode;   // has a real web leaderboard
  const timeframeLocked = !isLiveMode || isKnockoutMode;                 // Knockout is all-time only

  const userEntry = entries.find(e => e.isCurrentUser);
  const userRank = userEntry ? entries.indexOf(userEntry) + 1 : null;

  function closeDropdowns() {
    setShowGameModeDropdown(false);
    setShowTimeframeDropdown(false);
  }

  return (
    <div style={styles.page} onClick={closeDropdowns}>

      {/* Champions (trophy) button — pinned to the top bar's empty right slot
          (mirrors the left menu button) so it never overlaps the filters. */}
      <div style={styles.trophyWrap}>
        <button
          style={styles.trophyBtn}
          onClick={(e) => { e.stopPropagation(); setShowChampions(true); }}
          aria-label="Daily champions"
          title="Champions"
        >
          <span
            style={{
              ...styles.trophyIcon,
              WebkitMaskImage: `url(${trophyFill})`,
              maskImage: `url(${trophyFill})`,
            }}
          />
        </button>
      </div>

      <div style={styles.container}>

        {/* Top gap below TopBar */}
        <div style={{ paddingTop: "12px" }} />

        {/* ── Sport Filter Row ── */}
        <div style={{
          ...styles.sportRow,
          opacity: hasAppeared ? 1 : 0,
          transform: hasAppeared ? "translateY(0)" : "translateY(10px)",
          transition: "all 0.4s ease 0.1s",
        }}>
          {SPORTS.map(sport => (
            <SportFilterButton
              key={sport.id}
              sport={sport}
              isSelected={selectedSport.id === sport.id}
              onSelect={() => { setSelectedSport(sport); closeDropdowns(); }}
            />
          ))}
        </div>

        {/* ── Filters Row (dropdowns) ── */}
        <div style={{
          ...styles.filtersRow,
          opacity: hasAppeared ? 1 : 0,
          transition: "opacity 0.4s ease 0.15s",
          zIndex: 100,
          position: "relative",
        }}
          onClick={e => e.stopPropagation()}
        >
          {/* Game mode dropdown */}
          <div style={{ flex: 1, position: "relative" }}>
            <DropdownTrigger
              label={selectedGameMode}
              isOpen={showGameModeDropdown}
              accent={APP_THEME}
              onClick={() => {
                setShowGameModeDropdown(v => !v);
                setShowTimeframeDropdown(false);
              }}
            />
            {showGameModeDropdown && (
              <div style={styles.dropdownMenu}>
                {GAME_MODES.map((mode, i) => (
                  <React.Fragment key={mode}>
                    <button
                      style={{
                        ...styles.dropdownItem,
                        color: selectedGameMode === mode ? APP_THEME : "rgba(255,255,255,0.85)",
                      }}
                      onClick={() => { setSelectedGameMode(mode); setShowGameModeDropdown(false); }}
                    >
                      <span style={{ flex: 1, textAlign: "left" }}>{mode}</span>
                      {selectedGameMode === mode && <span style={{ color: APP_THEME }}>✓</span>}
                    </button>
                    {i < GAME_MODES.length - 1 && <div style={styles.dropdownDivider} />}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          {/* Timeframe dropdown */}
          <div style={{ flex: 1, position: "relative" }}>
            <DropdownTrigger
              label={timeframeLocked ? (isDraftsMode ? "DAILY" : "ALL-TIME") : selectedTimeframe}
              isOpen={showTimeframeDropdown}
              isLocked={timeframeLocked}
              accent={APP_THEME}
              onClick={() => {
                if (timeframeLocked) return;
                setShowTimeframeDropdown(v => !v);
                setShowGameModeDropdown(false);
              }}
            />
            {showTimeframeDropdown && !timeframeLocked && (
              <div style={{ ...styles.dropdownMenu, right: 0, left: "auto" }}>
                {TIMEFRAMES.map((tf, i) => (
                  <React.Fragment key={tf}>
                    <button
                      style={{
                        ...styles.dropdownItem,
                        color: selectedTimeframe === tf ? APP_THEME : "rgba(255,255,255,0.85)",
                      }}
                      onClick={() => { setSelectedTimeframe(tf); setShowTimeframeDropdown(false); }}
                    >
                      <span style={{ flex: 1, textAlign: "left" }}>{tf}</span>
                      {selectedTimeframe === tf && <span style={{ color: APP_THEME }}>✓</span>}
                    </button>
                    {i < TIMEFRAMES.length - 1 && <div style={styles.dropdownDivider} />}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Leaderboard Content ── */}
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          opacity: hasAppeared ? 1 : 0,
          transition: "opacity 0.4s ease 0.2s",
        }}>
          {isPickUpMode ? (
            <>
              {userEntry && userEntry.gamesPlayed > 0 && (
                <PickUpStatCard rank={userRank} record={userEntry} sport={selectedSport} />
              )}
              <PickUpLeaderboardPanel
                entries={entries}
                sport={selectedSport}
                isLoading={isLoadingData}
                loadError={loadError}
                onRetry={loadData}
              />
            </>
          ) : (
            <>
              {/* User stat card — only shown when user has a score */}
              {isLiveMode && userEntry && userRank && (
                <UserStatCard
                  rank={userRank}
                  score={userEntry.teamRating}
                  sport={selectedSport}
                  scoreLabel={isKnockoutMode ? "YOUR WINS" : "YOUR SCORE"}
                />
              )}

              {/* Leaderboard panel */}
              <LeaderboardPanel
                entries={entries}
                sport={selectedSport}
                isLiveMode={isLiveMode}
                isStreaksMode={isStreaksMode}
                gameMode={selectedGameMode}
                isLoading={isLoadingData}
                loadError={loadError}
                lastUpdated={lastUpdated}
                onRetry={loadData}
              />
            </>
          )}
        </div>

      </div>

      {showChampions && (
        <DailyChampionsModal onClose={() => setShowChampions(false)} />
      )}
    </div>
  );
}

// ─── Sport Filter Button ──────────────────────────────────────────────────────
// Mirrors sportFilterButton() in LeaderboardView.swift

function SportFilterButton({ sport, isSelected, onSelect }) {
  return (
    <button
      style={{
        ...styles.sportBtn,
        background: isSelected ? sport.hex : "rgba(255,255,255,0.06)",
        border: isSelected ? "none" : "1px solid rgba(255,255,255,0.1)",
        boxShadow: isSelected ? `0 4px 12px ${sport.hex}73` : "none",
        transform: isSelected ? "scale(1.04)" : "scale(1)",
        transition: "all 0.2s ease",
      }}
      onClick={onSelect}
    >
      <img
        src={SPORT_ICONS[sport.id]}
        alt={sport.name}
        style={{
          width: 26,
          height: 26,
          // Black on the sport-colored background when selected (matches Home
          // and Draft); white and slightly dimmed on the dark background when not.
          filter: isSelected ? "brightness(0)" : "brightness(0) invert(1)",
          opacity: isSelected ? 1 : 0.6,
        }}
      />
    </button>
  );
}

// ─── Dropdown Trigger ─────────────────────────────────────────────────────────

function DropdownTrigger({ label, isOpen, isLocked, accent, onClick }) {
  return (
    <button
      style={{
        ...styles.dropdownTrigger,
        background: isLocked ? "rgba(255,255,255,0.02)" : (isOpen ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.06)"),
        borderColor: isOpen && !isLocked ? `${accent}99` : "rgba(255,255,255,0.1)",
        opacity: isLocked ? 0.5 : 1,
        cursor: isLocked ? "default" : "pointer",
      }}
      onClick={onClick}
    >
      <span style={{ ...styles.dropdownTriggerLabel, color: isLocked ? "rgba(255,255,255,0.35)" : "#fff" }}>
        {label}
      </span>
      <span style={{ fontSize: 10, color: "rgba(255,255,255,0.5)" }}>
        {isOpen ? "▲" : "▼"}
      </span>
    </button>
  );
}

// ─── User Stat Card ───────────────────────────────────────────────────────────
// Mirrors leaderboardStatCard() in LeaderboardView_DraftStreak.swift

function UserStatCard({ rank, score, sport, scoreLabel = "YOUR SCORE" }) {
  return (
    <div style={{
      ...styles.userCard,
      background: `${sport.hex}1a`,
      border: `1px solid ${sport.hex}59`,
    }}>
      <div style={styles.userCardCell}>
        <span style={{ ...styles.userCardBigNum, color: sport.hex }}>#{rank}</span>
        <span style={styles.userCardCaption}>YOUR RANK</span>
      </div>
      <div style={styles.userCardDivider} />
      <div style={styles.userCardCell}>
        <span style={styles.userCardBigNum}>{score}</span>
        <span style={styles.userCardCaption}>{scoreLabel}</span>
      </div>
    </div>
  );
}

// ─── Leaderboard Panel ────────────────────────────────────────────────────────
// Mirrors leaderboardListPanel in LeaderboardView_DraftStreak.swift

function LeaderboardPanel({ entries, sport, isLiveMode, isStreaksMode, gameMode, isLoading, loadError, lastUpdated, onRetry }) {
  const isKnockoutMode = gameMode === "KNOCKOUT";
  function footerText() {
    if (isKnockoutMode) return "Leaderboard updates when a pool completes";
    if (!lastUpdated) return "Leaderboard updates every 30 minutes";
    const minutes = Math.floor((Date.now() - lastUpdated.getTime()) / 60000);
    if (minutes < 1) return "Updated just now";
    if (minutes === 1) return "Updated 1 minute ago";
    return `Updated ${minutes} minutes ago`;
  }

  return (
    <div style={styles.panel}>
      <div style={styles.columnHeader}>
        <span style={{ ...styles.columnLabel, width: 42 }}>RANK</span>
        <span style={styles.columnLabel}>PLAYER</span>
        <span style={{ ...styles.columnLabel, marginLeft: "auto" }}>
          {isLiveMode ? (isKnockoutMode ? "WINS" : isStreaksMode ? "STREAK" : "SCORE") : gameMode}
        </span>
      </div>

      <div style={styles.panelDivider} />

      {!isLiveMode ? (
        <ComingSoonState gameMode={gameMode} />
      ) : isLoading ? (
        <LoadingState />
      ) : loadError ? (
        <ErrorState message={loadError} onRetry={onRetry} />
      ) : entries.length === 0 ? (
        <EmptyState isStreaksMode={isStreaksMode} isKnockoutMode={isKnockoutMode} />
      ) : (
        <div style={styles.entryList}>
          {entries.map((entry, index) => (
            <React.Fragment key={entry.id}>
              <EntryRow rank={index + 1} entry={entry} sport={sport} />
              {index < entries.length - 1 && <div style={styles.rowDivider} />}
            </React.Fragment>
          ))}
        </div>
      )}

      {isLiveMode && entries.length > 0 && !isLoading && (
        <div style={styles.footer}>
          <span style={styles.footerText}>↻ {footerText()}</span>
        </div>
      )}
    </div>
  );
}

// ─── Entry Row ────────────────────────────────────────────────────────────────
// Mirrors draftEntryRow() in LeaderboardView_DraftStreak.swift

function EntryRow({ rank, entry, sport }) {
  return (
    <div style={{
      ...styles.entryRow,
      background: entry.isCurrentUser ? `${sport.hex}1a` : "transparent",
    }}>
      {/* Rank badge */}
      <div style={{
        ...styles.rankBadge,
        background: rank <= 3 ? `${sport.hex}26` : "transparent",
      }}>
        <span style={{
          ...styles.rankText,
          color: rank <= 3 ? sport.hex : "rgba(255,255,255,0.4)",
        }}>
          {rank}
        </span>
      </div>

      {/* Username */}
      <span style={{
        ...styles.usernameText,
        fontWeight: entry.isCurrentUser ? "700" : "500",
        color: entry.isCurrentUser ? sport.hex : "rgba(255,255,255,0.85)",
        flex: 1,
      }}>
        {entry.username}
      </span>

      {/* Score */}
      <span style={{
        ...styles.scoreText,
        color: entry.isCurrentUser ? sport.hex : "#ffffff",
      }}>
        {entry.teamRating}
      </span>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function LoadingState() {
  return (
    <div style={styles.emptyState}>
      <span style={{ fontSize: 28, opacity: 0.4 }}>⏳</span>
      <span style={styles.emptyTitle}>Loading...</span>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div style={styles.emptyState}>
      <span style={{ fontSize: 28, opacity: 0.4 }}>⚠️</span>
      <span style={styles.emptyTitle}>Couldn't Load</span>
      <span style={styles.emptySubtitle}>{message}</span>
      <button
        style={styles.retryBtn}
        onClick={onRetry}
      >
        RETRY
      </button>
    </div>
  );
}

function EmptyState({ isStreaksMode, isKnockoutMode }) {
  if (isKnockoutMode) {
    return (
      <div style={styles.emptyState}>
        <span style={{ fontSize: 32, opacity: 0.3 }}>🛡️</span>
        <span style={styles.emptyTitle}>No Winners Yet</span>
        <span style={styles.emptySubtitle}>The first Knockout champion will appear here.</span>
      </div>
    );
  }
  return (
    <div style={styles.emptyState}>
      <span style={{ fontSize: 32, opacity: 0.3 }}>🏆</span>
      <span style={styles.emptyTitle}>{isStreaksMode ? "No Streaks Yet" : "No Drafts Yet"}</span>
      <span style={styles.emptySubtitle}>Be the first on the board!</span>
    </div>
  );
}

// ─── Coming Soon State ────────────────────────────────────────────────────────

function ComingSoonState({ gameMode }) {
  return (
    <div style={styles.emptyState}>
      <span style={{ fontSize: 32, opacity: 0.3 }}>🔒</span>
      <span style={styles.emptyTitle}>{gameMode} Leaderboard</span>
      <span style={styles.emptySubtitle}>Currently only available in the iOS app version. Coming soon to web.</span>
    </div>
  );
}

// ─── Pick Up Leaderboard ──────────────────────────────────────────────────────
// Mirrors LeaderboardView_PickUp.swift — all-time W-L standings from pickup_standings.

function PickUpStatCard({ rank, record, sport }) {
  return (
    <div style={{ ...styles.userCard, background: `${sport.hex}1a`, border: `1px solid ${sport.hex}59` }}>
      <div style={styles.userCardCell}>
        <span style={{ ...styles.userCardBigNum, color: sport.hex }}>{rank ? `#${rank}` : "—"}</span>
        <span style={styles.userCardCaption}>YOUR RANK</span>
      </div>
      <div style={styles.userCardDivider} />
      <div style={styles.userCardCell}>
        <span style={styles.userCardBigNum}>{record.wins}-{record.losses}</span>
        <span style={styles.userCardCaption}>RECORD</span>
      </div>
    </div>
  );
}

function PickUpLeaderboardPanel({ entries, sport, isLoading, loadError, onRetry }) {
  return (
    <div style={styles.panel}>
      <div style={styles.columnHeader}>
        <span style={{ ...styles.columnLabel, width: 42 }}>RANK</span>
        <span style={styles.columnLabel}>PLAYER</span>
        <span style={{ ...styles.columnLabel, marginLeft: "auto" }}>RECORD</span>
      </div>

      <div style={styles.panelDivider} />

      {isLoading ? (
        <LoadingState />
      ) : loadError ? (
        <ErrorState message={loadError} onRetry={onRetry} />
      ) : entries.length === 0 ? (
        <div style={styles.emptyState}>
          <span style={{ fontSize: 32, opacity: 0.3 }}>⚔️</span>
          <span style={styles.emptyTitle}>No Games Yet</span>
          <span style={styles.emptySubtitle}>Be the first on the board!</span>
        </div>
      ) : (
        <div style={styles.entryList}>
          {entries.map((entry, index) => (
            <React.Fragment key={entry.id}>
              <PickUpEntryRow rank={index + 1} entry={entry} sport={sport} />
              {index < entries.length - 1 && <div style={styles.rowDivider} />}
            </React.Fragment>
          ))}
        </div>
      )}

      {entries.length > 0 && !isLoading && (
        <div style={styles.footer}>
          <span style={styles.footerText}>↻ Leaderboard updates after each game</span>
        </div>
      )}
    </div>
  );
}

function PickUpEntryRow({ rank, entry, sport }) {
  const cu = entry.isCurrentUser;
  const pct = entry.gamesPlayed > 0 ? `${Math.round(entry.winPct)}%` : "—";
  return (
    <div style={{ ...styles.entryRow, background: cu ? `${sport.hex}1a` : "transparent" }}>
      <div style={{ ...styles.rankBadge, background: rank <= 3 ? `${sport.hex}26` : "transparent" }}>
        <span style={{ ...styles.rankText, color: rank <= 3 ? sport.hex : "rgba(255,255,255,0.4)" }}>{rank}</span>
      </div>
      <span style={{
        ...styles.usernameText,
        fontWeight: cu ? "700" : "500",
        color: cu ? sport.hex : "rgba(255,255,255,0.85)",
        flex: 1,
      }}>
        {entry.username}
      </span>
      <span style={{ ...styles.recordText, color: cu ? sport.hex : "#ffffff" }}>{entry.wins}-{entry.losses}</span>
      <span style={{ ...styles.pctText, color: cu ? `${sport.hex}b3` : "rgba(255,255,255,0.4)" }}>{pct}</span>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight: "100vh",
  },
  container: {
    maxWidth: "480px",
    margin: "0 auto",
    padding: "0 20px 48px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: "52px",
    paddingBottom: "6px",
  },
  wordmark: { height: "27px", width: "auto" },
  trophyWrap: {
    // Fixed, centered to the same 480px column as the TopBar so the button lands
    // in the TopBar's empty right slot on any screen width. height:0 +
    // pointerEvents:none keeps it from blocking anything but the button itself.
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    maxWidth: "480px",
    margin: "0 auto",
    height: 0,
    pointerEvents: "none",
    zIndex: 40,
  },
  trophyBtn: {
    position: "absolute",
    top: "14px",   // matches the TopBar's 14px top padding (aligns with menu button)
    right: "16px", // matches the TopBar's 16px horizontal padding
    width: 40,
    height: 40,
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.1)",
    background: "rgba(255,255,255,0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    pointerEvents: "auto",
  },
  trophyIcon: {
    width: 20,
    height: 20,
    display: "inline-block",
    background: "rgba(255,255,255,0.75)",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskSize: "contain",
    maskSize: "contain",
    WebkitMaskPosition: "center",
    maskPosition: "center",
  },

  // Sport filter row
  sportRow: {
    display: "flex",
    gap: "8px",
  },
  sportBtn: {
    flex: 1,
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },

  // Filters row
  filtersRow: {
    display: "flex",
    gap: "10px",
    height: "38px",
  },
  dropdownTrigger: {
    width: "100%",
    height: "38px",
    borderRadius: "12px",
    border: "1px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 14px",
    gap: "6px",
  },
  dropdownTriggerLabel: {
    fontSize: "13px",
    fontWeight: "700",
    letterSpacing: "0.4px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
    textAlign: "left",
  },
  dropdownMenu: {
    position: "absolute",
    top: "42px",
    left: 0,
    minWidth: "180px",
    background: "rgb(33,33,33)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "12px",
    boxShadow: "0 10px 40px rgba(0,0,0,0.6)",
    zIndex: 200,
    overflow: "hidden",
  },
  dropdownItem: {
    width: "100%",
    padding: "13px 16px",
    background: "none",
    border: "none",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    textAlign: "left",
  },
  dropdownDivider: {
    height: "1px",
    background: "rgba(255,255,255,0.08)",
    margin: "0 12px",
  },

  // User stat card
  userCard: {
    borderRadius: "14px",
    display: "flex",
    alignItems: "center",
    padding: "6px 0",
  },
  userCardCell: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "3px",
  },
  userCardDivider: {
    width: "1px",
    height: "40px",
    background: "rgba(255,255,255,0.1)",
  },
  userCardBigNum: {
    fontSize: "26px",
    fontWeight: "800",
    color: "#ffffff",
    lineHeight: 1,
  },
  userCardCaption: {
    fontSize: "10px",
    fontWeight: "700",
    color: "rgba(255,255,255,0.45)",
    letterSpacing: "1px",
  },

  // Leaderboard panel
  panel: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.09)",
    borderRadius: "16px",
    overflow: "hidden",
    minHeight: "360px",
    display: "flex",
    flexDirection: "column",
  },
  columnHeader: {
    display: "flex",
    alignItems: "center",
    padding: "6px 16px",
    gap: "10px",
  },
  columnLabel: {
    fontSize: "10px",
    fontWeight: "700",
    color: "rgba(255,255,255,0.35)",
    letterSpacing: "1px",
  },
  panelDivider: {
    height: "1px",
    background: "rgba(255,255,255,0.08)",
    margin: "0 8px",
  },
  entryList: {
    flex: 1,
    overflowY: "auto",
  },
  entryRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "8px 16px",
  },
  rowDivider: {
    height: "1px",
    background: "rgba(255,255,255,0.05)",
    margin: "0 16px",
  },
  rankBadge: {
    width: "32px",
    height: "22px",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  rankText: {
    fontSize: "13px",
    fontWeight: "800",
  },
  usernameText: {
    fontSize: "15px",
    letterSpacing: "0.2px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  scoreText: {
    fontSize: "15px",
    fontWeight: "700",
    width: "46px",
    textAlign: "right",
    flexShrink: 0,
  },
  retryBtn: {
    marginTop: "8px",
    padding: "8px 20px",
    borderRadius: "8px",
    border: `1px solid ${APP_THEME}66`,
    background: "transparent",
    color: APP_THEME,
    fontSize: "12px",
    fontWeight: "800",
    letterSpacing: "1px",
    cursor: "pointer",
  },
  emptyState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    padding: "48px 0",
  },
  emptyTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "rgba(255,255,255,0.4)",
  },
  emptySubtitle: {
    fontSize: "13px",
    fontWeight: "500",
    color: "rgba(255,255,255,0.25)",
  },
  footer: {
    padding: "5px 16px",
    display: "flex",
    justifyContent: "center",
  },
  footerText: {
    fontSize: "10px",
    fontWeight: "500",
    color: "rgba(255,255,255,0.25)",
    letterSpacing: "0.2px",
  },
  recordText: {
    fontSize: "15px",
    fontWeight: "700",
    width: "54px",
    textAlign: "right",
    flexShrink: 0,
  },
  pctText: {
    fontSize: "12px",
    fontWeight: "500",
    width: "40px",
    textAlign: "right",
    flexShrink: 0,
  },
};
